import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { usageAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { UsageDetail } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { AlertCircle, Loader2, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';

const TenantUsageDetailPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isConfigured } = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25);
  
  // Get dates from navigation state
  const { startDate, endDate } = location.state || {};

  const { data, isLoading, error } = useQuery(
    ['tenant-usage-detail', tenantId, page, startDate, endDate],
    () => usageAPI.getDetailedUsage({
      page: page + 1,
      limit: rowsPerPage,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      tenantId: tenantId
    }),
    {
      enabled: isConfigured && !!tenantId,
      keepPreviousData: true,
    }
  );

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const getQueryTypeLabel = (type: string): string => {
    switch (type) {
      case 'natural_language':
        return 'Natural Language';
      case 'follow_up':
        return 'Follow Up';
      case 'schema_exploration':
        return 'Schema';
      default:
        return type;
    }
  };

  const getProviderColor = (provider: string): 'default' | 'secondary' | 'outline' => {
    switch (provider) {
      case 'openai':
        return 'default';
      case 'anthropic':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  if (!isConfigured) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Please complete the initial setup to view usage data.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/usage')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tenant Overview
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Tenant Usage Details</h1>
        <p className="text-muted-foreground">
          Viewing detailed usage for tenant: {tenantId}
        </p>
      </div>

      {/* Date Range Info */}
      {(startDate || endDate) && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 text-sm">
              <span className="font-medium">Date Range:</span>
              <span className="text-muted-foreground">
                {startDate || 'Beginning'} - {endDate || 'Now'}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage Table */}
      <Card>
        <CardHeader>
          <CardTitle>Query History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load usage data. Please try again.
              </AlertDescription>
            </Alert>
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Conversation ID</TableHead>
                      <TableHead>Query Type</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.data.map((usage: UsageDetail) => (
                      <TableRow key={usage.id}>
                        <TableCell>
                          {format(new Date(usage.timestamp), 'MMM d, HH:mm')}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {usage.conversationId.slice(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {getQueryTypeLabel(usage.queryType)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getProviderColor(usage.provider)}>
                            {usage.provider}
                          </Badge>
                        </TableCell>
                        <TableCell>{usage.model}</TableCell>
                        <TableCell className="text-right">{usage.totalTokens.toLocaleString()}</TableCell>
                        <TableCell className="text-right">${usage.cost.toFixed(4)}</TableCell>
                        <TableCell>
                          <Badge variant={usage.success ? 'default' : 'destructive'}>
                            {usage.success ? 'Success' : 'Failed'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Page {page + 1} of {data.pages || 1}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChangePage(page - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChangePage(page + 1)}
                    disabled={!data || page >= data.pages - 1}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No usage data found for this tenant.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantUsageDetailPage;